import { RPACourse } from '../types';

export const MULTI_PLATFORM_COURSES: RPACourse[] = [
  {
    id: 'automation-anywhere-development',
    title: 'Automation Anywhere Bot Development',
    description: 'Master Automation Anywhere A360 platform for enterprise RPA development, from basic bots to complex automation solutions.',
    level: 'intermediate',
    duration: '13 hours',
    category: 'multi-platform',
    platform: 'Automation Anywhere A360',
    certificationPrep: true,
    prerequisites: [],
    learningObjectives: [
      'Navigate Automation Anywhere A360 interface and create bots',
      'Implement web, desktop, and API automation',
      'Design reusable bot components and libraries',
      'Integrate with databases and enterprise systems',
      'Deploy and manage bots in Control Room'
    ],
    tags: ['Automation Anywhere', 'A360', 'Bot Development', 'Enterprise RPA'],
    modules: [
      {
        id: 'aa-platform-overview',
        title: 'Automation Anywhere A360 Platform Overview',
        description: 'Introduction to AA A360 architecture and development environment',
        content: 'Platform architecture, Bot Editor interface, and development fundamentals.',
        duration: '2 hours',
        difficulty: 'beginner'
      },
      {
        id: 'bot-development-basics',
        title: 'Bot Development Fundamentals',
        description: 'Create basic bots with variables, commands, and control structures',
        content: 'Bot creation, variable management, and basic automation commands.',
        duration: '3 hours',
        difficulty: 'beginner'
      },
      {
        id: 'web-desktop-automation-aa',
        title: 'Web and Desktop Automation',
        description: 'Master object cloning, web automation, and desktop application interaction',
        content: 'Object cloning techniques, web automation strategies, and desktop app integration.',
        duration: '3 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'data-processing-aa',
        title: 'Advanced Data Processing and Integration',
        description: 'Handle complex data operations, Excel automation, and database connectivity',
        content: 'Data manipulation, Excel operations, database integration, and API consumption.',
        duration: '2.5 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'reusable-components',
        title: 'Reusable Components and Bot Libraries',
        description: 'Create modular, reusable bot components and manage bot libraries',
        content: 'Component design, bot libraries, and code reusability patterns.',
        duration: '1.5 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'deployment-management',
        title: 'Bot Deployment and Control Room Management',
        description: 'Deploy, schedule, and monitor bots through Control Room',
        content: 'Deployment strategies, scheduling, monitoring, and bot lifecycle management.',
        duration: '1 hour',
        difficulty: 'intermediate'
      }
    ]
  },
  {
    id: 'blue-prism-development',
    title: 'Blue Prism Process Design & Development',
    description: 'Learn Blue Prism\'s object-oriented approach to RPA development, process design, and enterprise automation.',
    level: 'intermediate',
    duration: '12 hours',
    category: 'multi-platform',
    platform: 'Blue Prism',
    certificationPrep: true,
    prerequisites: [],
    learningObjectives: [
      'Understand Blue Prism\'s object-oriented automation approach',
      'Design and develop business objects and process flows',
      'Implement exception handling and process recovery',
      'Create scalable automation architectures',
      'Manage work queues and resource optimization'
    ],
    tags: ['Blue Prism', 'Object-Oriented RPA', 'Process Design', 'Enterprise Automation'],
    modules: [
      {
        id: 'bp-fundamentals',
        title: 'Blue Prism Fundamentals and Architecture',
        description: 'Understand Blue Prism\'s unique architecture and design principles',
        content: 'Platform architecture, object-oriented approach, and development environment setup.',
        duration: '2 hours',
        difficulty: 'beginner'
      },
      {
        id: 'business-objects',
        title: 'Business Object Development',
        description: 'Create reusable business objects for application interaction',
        content: 'Business object design, application modelling, and interface creation.',
        duration: '3 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'process-design',
        title: 'Process Design and Flow Control',
        description: 'Design robust process flows with proper control structures',
        content: 'Process flow design, decision logic, loop structures, and flow optimization.',
        duration: '2.5 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'exception-handling-bp',
        title: 'Exception Handling and Process Recovery',
        description: 'Implement comprehensive exception handling and recovery mechanisms',
        content: 'Exception strategies, error handling, recovery procedures, and resilient design.',
        duration: '2 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'work-queues',
        title: 'Work Queues and Resource Management',
        description: 'Manage work distribution and resource optimization',
        content: 'Work queue design, load balancing, resource management, and scalability planning.',
        duration: '1.5 hours',
        difficulty: 'intermediate'
      },
      {
        id: 'deployment-monitoring',
        title: 'Deployment and Process Monitoring',
        description: 'Deploy processes and implement monitoring strategies',
        content: 'Deployment procedures, monitoring setup, performance analysis, and maintenance.',
        duration: '1 hour',
        difficulty: 'intermediate'
      }
    ]
  },
  {
    id: 'rpa-architecture-governance',
    title: 'RPA Architecture & Governance',
    description: 'Design enterprise RPA architectures, establish governance frameworks, and implement best practices across platforms.',
    level: 'advanced',
    duration: '15 hours',
    category: 'multi-platform',
    platform: 'Platform Agnostic',
    certificationPrep: false,
    prerequisites: ['automation-anywhere-development', 'blue-prism-development'],
    learningObjectives: [
      'Design scalable enterprise RPA architectures',
      'Establish comprehensive governance frameworks',
      'Implement cross-platform integration strategies',
      'Define operational excellence practices',
      'Create center of excellence (CoE) programs'
    ],
    tags: ['RPA Architecture', 'Governance', 'Enterprise', 'CoE', 'Best Practices'],
    modules: [
      {
        id: 'enterprise-architecture',
        title: 'Enterprise RPA Architecture Design',
        description: 'Design scalable and resilient RPA architectures for large organizations',
        content: `
# Enterprise RPA Architecture Design

## Enterprise RPA Architecture Fundamentals

### Strategic Architecture Considerations

**Business Alignment:**
- Align RPA strategy with business objectives
- Identify high-value automation opportunities
- Define success metrics and KPIs
- Establish governance framework

**Technology Selection:**
- Evaluate RPA platforms (UiPath, Blue Prism, Automation Anywhere, Power Automate)
- Consider hybrid automation approaches
- Assess integration requirements
- Plan for technology evolution

**Scalability Planning:**
- Design for current and future automation volumes
- Plan infrastructure capacity
- Consider geographic distribution
- Account for peak load scenarios

## Architecture Patterns

### Centralized Architecture

**Hub-and-Spoke Model:**
- Central orchestration layer
- Distributed execution nodes
- Centralized monitoring and control
- Single source of truth for configuration

**Benefits:**
- Strong governance and control
- Consistent standards across organization
- Efficient resource utilization
- Simplified monitoring and management

**Challenges:**
- Potential bottlenecks at central hub
- Requires robust network infrastructure
- May reduce local autonomy

### Federated Architecture

**Distributed Control Model:**
- Business unit autonomy
- Local RPA teams and infrastructure
- Central policy and standards
- Federated governance

**Benefits:**
- Faster deployment within units
- Better alignment with local needs
- Reduced single points of failure
- Increased organizational buy-in

**Challenges:**
- Requires strong governance framework
- Risk of inconsistent implementations
- Potential duplication of effort

### Hybrid Architecture (Recommended)

**Combined Approach:**
- Central CoE for strategy and standards
- Distributed implementation teams
- Shared infrastructure with local nodes
- Centralized monitoring, distributed execution

**Key Components:**
- Enterprise orchestration platform
- Regional execution environments
- Centralized credential vault
- Unified monitoring dashboard

## Infrastructure Planning

### On-Premises Infrastructure

**Components:**
- RPA server/orchestrator infrastructure
- Bot execution machines (virtual or physical)
- Database servers for orchestrator
- File servers for process artifacts
- Network infrastructure and firewalls

**Sizing Considerations:**
- Number of concurrent bot executions
- Transaction volumes and peak loads
- High availability requirements
- Disaster recovery needs

**Example Configuration (Medium Enterprise):**
- Orchestrator: 2 servers (active-passive HA)
- Database: SQL Server cluster (3 nodes)
- Bot Runners: 50 virtual machines
- Credential Vault: HashiCorp Vault cluster
- Monitoring: ELK Stack or Splunk


### Cloud Infrastructure

**Cloud Benefits:**
- Elastic scalability
- Pay-as-you-go pricing
- Built-in redundancy
- Global distribution

**Cloud Architecture Patterns:**
- Multi-region deployment for resilience
- Auto-scaling bot runner pools
- Cloud-native credential management
- Serverless orchestration where possible

**Hybrid Cloud Approach:**
- Cloud orchestration with on-premises execution
- On-premises critical systems, cloud for scaling
- Cloud development/test, on-premises production
- Multi-cloud strategy for redundancy

### High Availability Design

**Orchestrator HA:**
- Active-passive or active-active configuration
- Load balancing across instances
- Database clustering and replication
- Automated failover procedures

**Bot Runner Redundancy:**
- Multiple bots per process
- Geographic distribution
- Queue-based work distribution
- Failover bot pools

**Network Resilience:**
- Redundant network paths
- VPN and ExpressRoute redundancy
- WAN optimization
- Offline bot capabilities

## Security Architecture

### Zero Trust Security Model

**Core Principles:**
- Never trust, always verify
- Least privilege access
- Assume breach mentality
- Continuous monitoring

**Implementation:**
- Multi-factor authentication for all access
- Encrypted communication channels
- Role-based access control (RBAC)
- Regular security audits

### Credential Management

**Enterprise Credential Vault:**
- Centralized credential storage
- Encrypted at rest and in transit
- Role-based credential access
- Credential rotation automation

**Best Practices:**
- Service accounts for bot authentication
- No hard-coded credentials
- Automated password rotation
- Audit logging for all credential access

### Data Protection

**Encryption Strategy:**
- Encryption at rest for all data stores
- TLS for all network communications
- Tokenization for sensitive data
- Data masking in logs and screenshots

**Compliance Considerations:**
- GDPR, HIPAA, PCI-DSS alignment
- Data residency requirements
- Audit trail requirements
- Right to be forgotten

## Scalability Design

### Horizontal Scaling

**Bot Pool Management:**
- Dynamic bot pool sizing
- Auto-scaling based on queue depth
- Load balancing algorithms
- Resource optimization

**Queue Architecture:**
- Distributed queue system
- Priority-based processing
- Queue monitoring and alerting
- Dead letter queue handling

### Vertical Scaling

**Resource Optimization:**
- Right-size bot runner machines
- Optimize process efficiency
- Memory and CPU profiling
- Storage optimization

**Performance Tuning:**
- Minimize wait times
- Optimize selectors and image recognition
- Efficient data handling
- Parallel processing where possible

### Geographic Distribution

**Multi-Region Strategy:**
- Regional bot pools for low latency
- Data sovereignty compliance
- Follow-the-sun support model
- Disaster recovery across regions

## Integration Architecture

### Application Integration

**Integration Patterns:**
- API-first integration
- Database direct connections
- File-based integration
- Screen scraping (last resort)

**Integration Technologies:**
- RESTful APIs
- SOAP web services
- Message queues (RabbitMQ, Azure Service Bus)
- ETL/ESB platforms

### System of Record Integration

**ERP Integration:**
- SAP, Oracle, Microsoft Dynamics
- Two-way synchronization
- Transaction integrity
- Error handling and reconciliation

**Legacy System Integration:**
- Mainframe connectivity
- Terminal emulation
- Middleware adapters
- Modernization pathways

## Monitoring and Observability

### Comprehensive Monitoring

**Infrastructure Monitoring:**
- Server health and performance
- Network connectivity
- Resource utilization
- Capacity trends

**Process Monitoring:**
- Bot execution status
- Success/failure rates
- Processing times
- Queue depths

**Business Monitoring:**
- Transaction volumes
- Business outcomes
- Exception handling
- SLA compliance

### Logging and Analytics

**Centralized Logging:**
- Structured logging standards
- Log aggregation platform
- Real-time log analysis
- Long-term log retention

**Analytics and Reporting:**
- Executive dashboards
- Operational metrics
- Predictive analytics
- ROI tracking

## Governance and Compliance

### Governance Framework

**RPA Center of Excellence:**
- Strategy and roadmap
- Standards and best practices
- Training and certification
- Process review and approval

**Change Management:**
- Process change control
- Version management
- Deployment approvals
- Rollback procedures

### Compliance Management

**Regulatory Compliance:**
- SOX compliance for financial processes
- Industry-specific regulations
- Audit trail requirements
- Data protection laws

**Internal Controls:**
- Segregation of duties
- Approval workflows
- Exception handling
- Regular audits

## Disaster Recovery and Business Continuity

### DR Strategy

**Recovery Objectives:**
- Recovery Time Objective (RTO)
- Recovery Point Objective (RPO)
- Business impact analysis
- Critical process prioritization

**DR Implementation:**
- Active-passive or active-active setup
- Regular DR testing
- Automated failover procedures
- Documentation and runbooks

### Backup Strategy

**What to Backup:**
- Orchestrator database
- Process packages and dependencies
- Configuration settings
- Credential vault

**Backup Procedures:**
- Automated daily backups
- Off-site backup storage
- Regular restoration testing
- Version retention policy

## Technology Selection Framework

### Platform Evaluation Criteria

**Functional Requirements:**
- Process automation capabilities
- Integration options
- Scalability features
- AI/ML capabilities

**Non-Functional Requirements:**
- Performance and reliability
- Security features
- Vendor support and roadmap
- Total cost of ownership

**Decision Matrix:**
| Platform | Scalability | Security | Integration | AI/ML | Cost |
|----------|-------------|----------|-------------|-------|------|
| UiPath | Excellent | Excellent | Excellent | Excellent | High |
| Blue Prism | Excellent | Excellent | Good | Good | High |
| Automation Anywhere | Excellent | Excellent | Excellent | Excellent | High |
| Power Automate | Good | Excellent | Excellent | Good | Medium |

## Best Practices Summary

1. **Start with Governance:** Establish CoE and standards before scaling
2. **Design for Scale:** Plan infrastructure for 3-5 year growth
3. **Security First:** Build security into architecture from day one
4. **Monitor Everything:** Comprehensive monitoring and logging
5. **Plan for Failure:** Implement HA and DR from the beginning
6. **Standardize:** Use consistent patterns and templates
7. **Document:** Maintain comprehensive architecture documentation
8. **Continuous Improvement:** Regular architecture reviews and updates
        `,
        duration: '4 hours',
        difficulty: 'advanced'
      },
      {
        id: 'governance-framework',
        title: 'RPA Governance Framework Implementation',
        description: 'Establish comprehensive governance policies and procedures',
        content: 'Governance models, policy development, compliance frameworks, and risk management.',
        duration: '3 hours',
        difficulty: 'advanced'
      },
      {
        id: 'coe-establishment',
        title: 'Center of Excellence (CoE) Development',
        description: 'Create and manage RPA Centers of Excellence',
        content: 'CoE structure, operating models, service delivery, and community building.',
        duration: '3 hours',
        difficulty: 'advanced'
      },
      {
        id: 'cross-platform-integration',
        title: 'Cross-Platform Integration Strategies',
        description: 'Integrate multiple RPA platforms and technologies',
        content: 'Integration patterns, API strategies, data exchange, and platform orchestration.',
        duration: '2.5 hours',
        difficulty: 'advanced'
      },
      {
        id: 'operational-excellence',
        title: 'Operational Excellence and Continuous Improvement',
        description: 'Implement operational excellence practices and continuous improvement',
        content: 'Operations management, performance optimization, continuous improvement, and maturity models.',
        duration: '2 hours',
        difficulty: 'advanced'
      },
      {
        id: 'change-management',
        title: 'Change Management and User Adoption',
        description: 'Manage organizational change and drive user adoption',
        content: 'Change management strategies, training programs, communication plans, and adoption metrics.',
        duration: '0.5 hours',
        difficulty: 'advanced'
      }
    ]
  },
  {
    id: 'cross-platform-implementation',
    title: 'Cross-Platform RPA Strategy & Implementation',
    description: 'Develop comprehensive strategies for implementing RPA across multiple platforms and technologies in enterprise environments.',
    level: 'advanced',
    duration: '14 hours',
    category: 'multi-platform',
    platform: 'Multi-Platform',
    certificationPrep: false,
    prerequisites: ['rpa-architecture-governance'],
    learningObjectives: [
      'Develop platform selection and migration strategies',
      'Implement cross-platform orchestration solutions',
      'Design hybrid automation architectures',
      'Establish unified monitoring and management',
      'Create platform-agnostic development standards'
    ],
    tags: ['Cross-Platform', 'Strategy', 'Migration', 'Orchestration', 'Standards'],
    modules: [
      {
        id: 'platform-strategy',
        title: 'Multi-Platform Strategy Development',
        description: 'Develop comprehensive strategies for multi-platform RPA environments',
        content: 'Strategic planning, platform evaluation, selection criteria, and migration strategies.',
        duration: '3 hours',
        difficulty: 'advanced'
      },
      {
        id: 'orchestration-solutions',
        title: 'Cross-Platform Orchestration',
        description: 'Implement orchestration solutions for multiple RPA platforms',
        content: 'Orchestration architectures, workflow management, and cross-platform communication.',
        duration: '3.5 hours',
        difficulty: 'advanced'
      },
      {
        id: 'hybrid-architectures',
        title: 'Hybrid Automation Architecture Design',
        description: 'Design architectures that leverage multiple automation technologies',
        content: 'Hybrid design patterns, technology integration, and architecture optimization.',
        duration: '3 hours',
        difficulty: 'advanced'
      },
      {
        id: 'unified-monitoring',
        title: 'Unified Monitoring and Management',
        description: 'Implement centralized monitoring across multiple platforms',
        content: 'Monitoring strategies, dashboard design, alerting systems, and performance management.',
        duration: '2.5 hours',
        difficulty: 'advanced'
      },
      {
        id: 'development-standards',
        title: 'Platform-Agnostic Development Standards',
        description: 'Establish consistent development practices across platforms',
        content: 'Coding standards, design patterns, documentation requirements, and quality assurance.',
        duration: '1.5 hours',
        difficulty: 'advanced'
      },
      {
        id: 'migration-strategies',
        title: 'Platform Migration and Modernization',
        description: 'Plan and execute platform migrations and modernization projects',
        content: 'Migration planning, risk mitigation, execution strategies, and modernization approaches.',
        duration: '0.5 hours',
        difficulty: 'advanced'
      }
    ]
  }
];